import { getPubNubConfig } from "../api/chatSettings";
import { getPubNubToken } from "../api/pubnubToken";
import {
  initPubNub,
  setPubNubToken,
  destroyPubNub,
  getPubNub,
  registerAccessDeniedHandler,
} from "../hooks/usePubNub";
import { useAuthStore } from "../store/authStore";
import logger from "../lib/logger";

// Prevents concurrent bootstrap calls from racing
let _inFlight: Promise<void> | null = null;
// Cache the config so refreshAndReinit doesn't have to re-fetch keys
let _cachedConfig: { pubKey: string; subKey: string } | null = null;

function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

export async function bootstrapPubNub(): Promise<void> {
  if (_inFlight) return _inFlight;

  _inFlight = _doBootstrap();
  try {
    await _inFlight;
  } finally {
    _inFlight = null;
  }
}

async function _doBootstrap(): Promise<void> {
  const userId = useAuthStore.getState().userId;
  if (!userId) return;

  // If already initialized AND has a token, skip
  const instance = getPubNub();
  if (instance && instance.getToken()) return;

  try {
    const [{ pubKey, subKey }, token] = await Promise.all([
      getPubNubConfig(),
      getPubNubToken(),
    ]);

    initPubNub(pubKey, subKey, userId);
    setPubNubToken(token);
    _cachedConfig = { pubKey, subKey };
    registerAccessDeniedHandler(refreshAndReinit);
  } catch (err) {
    // 401 is handled by the axios interceptor in client.ts (clears auth + redirects)
    // before the error reaches here, so no special 401 handling needed.
    logger.error("PubNub bootstrap failed", err);
    // Non-fatal — real-time will be unavailable until next retry
  }
}

/**
 * Full re-bootstrap mirroring Android's GetPubNubUseCase.fetchToken().
 * Fetches a new PAM token, destroys the current SDK instance, and re-inits.
 *
 * Note: the 2 s settle delay before returning (`sleep(2000)`) mirrors
 * Android's `delay(2000)`. Tests exercising this function must use
 * `vi.useFakeTimers()` to avoid a real 2-second wait.
 *
 * The token is fetched BEFORE destroying the instance so that a fetch failure
 * leaves the existing instance intact (destroy is only called on the happy path).
 *
 * Throws on failure — caller (fireMessage retry loop) handles errors.
 */
export async function refreshAndReinit(): Promise<void> {
  if (!_cachedConfig) {
    // No cached config — fall back to full bootstrap then apply the same settle delay
    await bootstrapPubNub();
    await sleep(2000);
    return;
  }

  const userId = useAuthStore.getState().userId;
  if (!userId) return;

  // Mirrors Android's GetPubNubUseCase.fetchToken()
  const token = await getPubNubToken();
  // Token fetch succeeded — now safe to destroy the old instance
  destroyPubNub();
  initPubNub(_cachedConfig.pubKey, _cachedConfig.subKey, userId);
  setPubNubToken(token);
  // Wait for PubNub to settle the new token (mirrors Android's delay(2000))
  await sleep(2000);
}
